import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { fileService } from '../services/api';
import { toast } from 'react-toastify';

// Función para normalizar tags (igual que en el backend)
const normalizeTag = (tag) => {
  if (!tag || typeof tag !== 'string') return null;
  return tag.toLowerCase().trim().replace(/\s+/g, '-');
};

const BulkTagsModal = ({ isOpen, onClose, files, onTagsUpdated }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPredefined, setLoadingPredefined] = useState(false);
  const [mode, setMode] = useState('replace'); // 'replace' o 'add'

  // Cargar tags predefinidos cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadPredefinedTags();
      setSelectedTags([]);
      setMode('replace');
    }
  }, [isOpen]);

  const loadPredefinedTags = async () => {
    setLoadingPredefined(true);
    try {
      const tags = await fileService.getPredefinedTags();
      setPredefinedTags(tags || []);
    } catch (error) {
      console.error('Error cargando tags predefinidos:', error);
      toast.error('Error al cargar tags predefinidos: ' + error.message, { autoClose: 2000 });
    } finally {
      setLoadingPredefined(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        // Remover tag
        return prev.filter(t => t !== tag);
      } else {
        // Agregar tag (máximo 6)
        if (prev.length >= 6) {
          toast.warning('Solo se pueden seleccionar máximo 6 tags', { autoClose: 2000 });
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!files || files.length === 0) {
      toast.error('No hay archivos seleccionados', { autoClose: 2000 });
      return;
    }

    if (selectedTags.length === 0 && mode === 'replace') {
      toast.warning('Selecciona al menos un tag o cambia el modo a "Agregar tags"', { autoClose: 2000 });
      return;
    }

    setLoading(true);

    try {
      const fileNames = files.map(file => file.Key || file.name);
      const result = await fileService.updateMultipleFileTags(fileNames, selectedTags, mode);
      
      if (result.success) {
        const successCount = result.successful || 0;
        const failedCount = result.failed || 0;
        
        if (failedCount === 0) {
          toast.success(`${successCount} archivo(s) etiquetado(s) correctamente`, { autoClose: 3000 });
        } else {
          toast.warning(`${successCount} archivo(s) etiquetado(s), ${failedCount} con errores`, { autoClose: 4000 });
        }
        
        if (onTagsUpdated) {
          onTagsUpdated();
        }

        onClose();
      } else {
        throw new Error(result.message || 'Error al actualizar tags');
      }
    } catch (error) {
      console.error('Error al actualizar tags:', error);
      toast.error('Error al actualizar tags: ' + error.message, { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const fileCount = files?.length || 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Etiquetar ${fileCount} archivo(s)`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {loadingPredefined ? (
          <div className="text-sm text-gray-400">Cargando tags disponibles...</div>
        ) : (
          <>
            {/* Modo de aplicación */}
            <div className="border border-gray-600 rounded-md p-4 bg-gray-800">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Modo de aplicación
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    value="replace"
                    checked={mode === 'replace'}
                    onChange={(e) => setMode(e.target.value)}
                    disabled={loading}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-300">Reemplazar tags</span>
                    <p className="text-xs text-gray-400">Los archivos quedarán solo con los tags seleccionados</p>
                  </div>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    value="add"
                    checked={mode === 'add'}
                    onChange={(e) => setMode(e.target.value)}
                    disabled={loading}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-300">Agregar tags</span>
                    <p className="text-xs text-gray-400">Se agregarán los tags seleccionados a los existentes</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Selección de tags */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Selecciona las etiquetas ({selectedTags.length}/6)
              </label>
              
              <div className="max-h-96 overflow-y-auto border border-gray-600 rounded-md p-4 bg-gray-800">
                <div className="flex flex-wrap gap-2">
                  {predefinedTags.map((tag) => {
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag)}
                        disabled={loading}
                        className={`
                          inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border
                          transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40
                          ${isSelected
                            ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                            : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500'
                          }
                          ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {tag}
                        {isSelected && (
                          <span className="ml-2 text-xs">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {selectedTags.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-2">Tags seleccionados:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleTagToggle(tag)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          disabled={loading}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Información */}
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-md p-3">
              <p className="text-xs text-blue-300">
                <strong>Archivos seleccionados:</strong> {fileCount} archivo(s)
              </p>
              <p className="text-xs text-blue-300 mt-1">
                {mode === 'replace' 
                  ? 'Los tags existentes serán reemplazados por los seleccionados.'
                  : 'Los tags seleccionados se agregarán a los tags existentes de cada archivo.'
                }
              </p>
            </div>
          </>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading || loadingPredefined}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-600 hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || loadingPredefined || (selectedTags.length === 0 && mode === 'replace')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? 'Guardando...' : 'Aplicar tags'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default BulkTagsModal;

